import { forwardRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { Icon } from '@/new/components/icon';
import { Button, ButtonProps } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

interface CopyIconButtonProps extends ButtonProps {
  getText: () => string;
  getHtml?: () => string;
  className?: string;
}

export const CopyIconButton = forwardRef<HTMLButtonElement, CopyIconButtonProps>(
  ({ getText, getHtml, className, ...props }, ref) => {
    const [copied, setCopied] = useState(false);

    const handleClick = async () => {
      if (getHtml) {
        const text = getText();
        const html = getHtml();
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': new Blob([text], { type: 'text/plain' }),
            'text/html': new Blob([html], { type: 'text/html' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(getText());
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };

    return (
      <Button
        ref={ref}
        variant="ghost"
        className="p-1 px-1.5"
        {...props}
        onClick={(e) => {
          props.onClick?.(e);
          handleClick();
        }}
      >
        <AnimatePresence mode="wait">
          {copied ? (
            <motion.span
              key="check"
              initial={{ opacity: 0, filter: 'blur(2px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, filter: 'blur(2px)' }}
              transition={{ duration: 0.1 }}
            >
              <Icon className={cn('lucide--check', className)} />
            </motion.span>
          ) : (
            <motion.span
              key="copy"
              initial={{ opacity: 0, filter: 'blur(2px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, filter: 'blur(2px)' }}
              transition={{ duration: 0.1 }}
            >
              <Icon className={cn('lucide--copy', className)} />
            </motion.span>
          )}
        </AnimatePresence>
      </Button>
    );
  },
);
CopyIconButton.displayName = 'CopyIconButton';
